import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerPosition,
} from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import 'air-datepicker/air-datepicker.css';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import closeIcon from '@/assets/icon/close-icon.svg';
import TimeFilter from '@/domains/traffic-monitoring/components/TimeFilter/TimeFilter';
import Button from '@/shared/components/ui/Button';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { formatDateToYYYYMMDD } from '@/shared/utils/date';
import { zoomSafeDatepickerPosition } from '@/shared/utils/datepickerPosition';
import type { ModalExportExcelProps } from './ModalExportExcel.types';

// Long-form, matching the export dialog design. The value sent to the backend
// is always ISO - see `formatDateToYYYYMMDD` at submit time.
const DISPLAY_FORMAT = 'MMMM dd, yyyy';
// A week ending today: useful on its own and comfortably inside the backend's
// 31-day ceiling, so the default range never opens on an error.
const DEFAULT_RANGE_DAYS = 7;

// Minutes per exported row - mirrors the backend's `interval` choices and
// default (see api-dsh's INTERVAL_MINUTES_CHOICES / DEFAULT_INTERVAL_MINUTES).
const INTERVAL_OPTIONS = ['15m', '30m', '1h', '1d'] as const;
type IntervalOption = (typeof INTERVAL_OPTIONS)[number];
const INTERVAL_MINUTES: Record<IntervalOption, string> = {
  '15m': '15',
  '30m': '30',
  '1h': '60',
  '1d': '1440',
};
const DEFAULT_INTERVAL: IntervalOption = '1h';

const localeFor = (language: string): AirDatepickerLocale => {
  if (language === 'eng') return localeEn;
  if (language === 'ind') return localeId;
  return localeKo;
};

const formatForDisplay = (date: Date, locale: AirDatepickerLocale) =>
  `${locale.months[date.getMonth()]} ${date.getDate().toString().padStart(2, '0')}, ${date.getFullYear()}`;

const defaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (DEFAULT_RANGE_DAYS - 1));
  return { start, end };
};

const ModalExportExcel = ({
  isOpen,
  onClose,
  title,
  fieldPrefix,
  onExport,
  isExporting = false,
  errorMessage,
}: ModalExportExcelProps) => {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState(defaultRange);
  const [interval, setIntervalOption] = useState<IntervalOption>(DEFAULT_INTERVAL);

  const startFieldName = `${fieldPrefix}_export_from`;
  const endFieldName = `${fieldPrefix}_export_to`;
  // Memoized so it can be a stable effect dependency: `localeFor` hands back
  // the module-level locale object, but a fresh call every render would still
  // re-run the effect and tear the pickers down under the operator.
  const locale = useMemo(() => localeFor(i18n.language), [i18n.language]);

  // Opening the dialog is what builds the pickers, so the range is reset to the
  // default here rather than read from state - that keeps the effect off
  // `range`, which changes on every pick and would destroy the open calendar.
  useEffect(() => {
    if (!isOpen) return;

    const initial = defaultRange();
    setRange(initial);
    setIntervalOption(DEFAULT_INTERVAL);

    const today = new Date();
    const build = (name: string, selected: Date, onPick: (picked: Date) => void) =>
      new AirDatepicker(`[name="${name}"]`, {
        autoClose: true,
        locale,
        // The calendar is appended to document.body, so it needs lifting over
        // the modal overlay - see `.adp-above-modal` in the stylesheet.
        classes: 'adp-above-modal',
        // Upward, so the calendar does not cover the Export button below it.
        // position: 'top center' as unknown as AirDatepickerPosition,
        position: zoomSafeDatepickerPosition('top center'),
        selectedDates: [selected],
        timepicker: false,
        dateFormat: DISPLAY_FORMAT,
        // Traffic data only ever exists up to now, so a future day would
        // always export an empty sheet.
        maxDate: today,
        onSelect: (selection) => {
          const picked = Array.isArray(selection.date) ? selection.date[0] : selection.date;
          if (picked) onPick(picked);
        },
      });

    const fromPicker = build(startFieldName, initial.start, (picked) =>
      setRange((prev) => ({ ...prev, start: picked }))
    );
    const toPicker = build(endFieldName, initial.end, (picked) =>
      setRange((prev) => ({ ...prev, end: picked }))
    );

    return () => {
      fromPicker.destroy();
      toPicker.destroy();
    };
  }, [isOpen, locale, startFieldName, endFieldName]);

  if (!isOpen) return null;

  const handleExport = () =>
    onExport(
      formatDateToYYYYMMDD(range.start),
      formatDateToYYYYMMDD(range.end),
      INTERVAL_MINUTES[interval]
    );

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[1000]">
      <div className="w-[440px] max-w-[90vw] p-6 flex flex-col gap-5 bg-db-card border bg-db-border rounded-[12px]">
        <div className="w-full flex flex-row gap-3 justify-between items-start">
          <span className="h5bold text-db-text">{title}</span>
          <button
            type="button"
            className="cursor-pointer"
            onClick={onClose}
            aria-label={t('cmn>master>close', 'Close')}
          >
            <img src={closeIcon} alt="" aria-hidden="true" />
          </button>
        </div>

        <div className="w-full flex flex-row gap-2 items-end">
          <div className="flex-1 flex flex-col gap-2">
            <span className="title2 text-db-text-grey">{t('cmn>master>from', 'From')}</span>
            <GeneralInput
              isDob={true}
              type="text"
              name={startFieldName}
              value={formatForDisplay(range.start, locale)}
              readonly={true}
              inputFormStyle="w-full bg-[#F6F6F6]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[8px]! h-[40px]! title2!"
            />
          </div>
          <span className="title2 text-db-text-grey pb-[10px]">~</span>
          <div className="flex-1 flex flex-col gap-2">
            <span className="title2 text-db-text-grey">{t('cmn>master>to', 'To')}</span>
            <GeneralInput
              isDob={true}
              type="text"
              name={endFieldName}
              value={formatForDisplay(range.end, locale)}
              readonly={true}
              inputFormStyle="w-full bg-[#F6F6F6]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[8px]! h-[40px]! title2!"
            />
          </div>
        </div>

        <div className="w-full flex flex-row gap-2 justify-between items-center">
          <span className="title2 text-db-text-grey">{t('cmn>master>interval', 'Interval')}</span>
          <TimeFilter
            options={INTERVAL_OPTIONS}
            value={interval}
            onChange={(value) => setIntervalOption(value as IntervalOption)}
          />
        </div>

        <div className="w-full flex flex-row gap-3 justify-between items-center">
          <span className="title2 text-text-danger-500 flex-1">{errorMessage}</span>
          <Button
            customButton="btn-search-dark min-w-[140px] cursor-pointer"
            label={
              isExporting
                ? t('cmn>master>exporting', 'Exporting...')
                : t('cmn>master>export', 'Export')
            }
            disabled={isExporting}
            onClick={handleExport}
          />
        </div>
      </div>
    </div>
  );
};

export default ModalExportExcel;
